import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { useAuth } from '../auth/auth-store';
import { formatDate, formatNumber, formatRelative } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { Person } from '../ui/Avatar';
import { useMembers } from '../ui/UserSelect';
import { NewTicketDialog } from './NewTicketDialog';
import {
  formatCountdown,
  listCategories,
  listTickets,
  slaTone,
  ticketSummary,
  type Ticket,
  type TicketQuery,
  type TicketSummary,
} from './api';

/**
 * The support queue.
 *
 * Ordered by what is late rather than by what is newest. A support screen sorted by arrival is a
 * screen where the chamado with four minutes left sits under nine that came in this morning, and a
 * team reading it top to bottom breaches the one that mattered while answering the ones that did
 * not.
 *
 * The figures above the table are the ones a supervisor actually asks about: how many are we owed,
 * how many has nobody picked up, how many are already late. "Sem responsável" is a button because it
 * is a to-do rather than a report — the whole point of noticing it is that somebody fixes it from
 * where they are reading.
 */

const TABS = ['open', 'mine', 'watching', 'unassigned', 'review', 'breached', 'all'] as const;

type Tab = (typeof TABS)[number];

function queryForTab(tab: Tab, userId: string | null): TicketQuery {
  if (tab === 'mine') return { openOnly: true, ...(userId ? { assigneeId: userId } : {}) };
  // Chamados somebody is following without answering them: the observer's own list.
  if (tab === 'watching') return userId ? { watcherId: userId } : {};
  if (tab === 'unassigned') return { openOnly: true, unassigned: true };
  // With the requester to accept. Not "open" — nothing is owed by us — but not finished either.
  if (tab === 'review') return { status: ['AWAITING_REVIEW'] };
  if (tab === 'breached') return { breachedOnly: true };
  if (tab === 'all') return {};

  return { openOnly: true };
}

export function TicketsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tab, setTab] = useState<Tab>('open');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [creating, setCreating] = useState(false);

  const summary = useQuery({ queryKey: ['ticket-summary'], queryFn: ticketSummary });

  const query = useMemo<TicketQuery>(
    () => ({
      ...queryForTab(tab, user?.id ?? null),
      ...(applied ? { search: applied } : {}),
      sort: 'urgency',
      limit: 50,
    }),
    [tab, applied, user?.id],
  );

  const tickets = useQuery({
    queryKey: ['tickets', tab, applied],
    queryFn: () => listTickets(query),
  });

  const { options: people } = useMembers();

  const categories = useQuery({
    queryKey: ['ticket-categories'],
    queryFn: listCategories,
    staleTime: 5 * 60 * 1000,
  });

  const categoryName = (id: string | null): string | null =>
    id === null
      ? null
      : ((categories.data?.categories ?? []).find((category) => category.id === id)?.name ?? null);

  const assignee = (id: string | null): { name: string; url?: string | null; muted: boolean } => {
    const found = id === null ? undefined : people.find((person) => person.id === id);

    return found
      ? { name: found.name, url: found.avatarUrl, muted: false }
      : { name: t('crm.unassigned'), muted: true };
  };

  const columns: Column<Ticket>[] = [
    {
      key: 'number',
      header: '#',
      align: 'end',
      render: (ticket) => <span className="muted">{ticket.number}</span>,
    },
    {
      key: 'subject',
      header: t('support.subject'),
      text: (ticket) =>
        [
          ticket.subject,
          categoryName(ticket.categoryId),
          ticket.requesterName,
          ticket.requesterEmail,
        ]
          .filter(Boolean)
          .join(' '),
      render: (ticket) => (
        <span className="cell-stack">
          <span className="strong">{ticket.subject}</span>
          <span className="muted small">
            {[categoryName(ticket.categoryId), ticket.requesterName ?? ticket.requesterEmail]
              .filter(Boolean)
              .join(' · ') || '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (ticket) => t(`support.status.${ticket.status}`),
      render: (ticket) => (
        <Badge tone={statusTone(ticket.status)}>{t(`support.status.${ticket.status}`)}</Badge>
      ),
    },
    {
      key: 'priority',
      header: t('support.priority'),
      text: (ticket) => t(`support.priority.${ticket.priority}`),
      render: (ticket) => (
        <span className={`priority priority--${ticket.priority.toLowerCase()}`}>
          {t(`support.priority.${ticket.priority}`)}
        </span>
      ),
    },
    {
      key: 'firstResponse',
      header: t('support.firstResponse'),
      text: (ticket) => slaText(ticket.sla.firstResponse, ticket.sla.minutesToFirstResponse),
      render: (ticket) => (
        <SlaCell health={ticket.sla.firstResponse} minutes={ticket.sla.minutesToFirstResponse} />
      ),
    },
    {
      key: 'resolution',
      header: t('support.resolution'),
      text: (ticket) => slaText(ticket.sla.resolution, ticket.sla.minutesToResolution),
      render: (ticket) => (
        <SlaCell health={ticket.sla.resolution} minutes={ticket.sla.minutesToResolution} />
      ),
    },
    {
      key: 'assignee',
      header: t('support.assignee'),
      text: (ticket) => assignee(ticket.assigneeId).name,
      render: (ticket) => (
        <span className="small">
          <Person size="xs" {...assignee(ticket.assigneeId)} />
        </span>
      ),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      text: (ticket) => formatRelative(ticket.createdAt),
      render: (ticket) => <span className="muted small">{formatRelative(ticket.createdAt)}</span>,
    },
    /*
     * Off by default, because the queue is read in a hurry and eight columns is already a lot.
     * They are the questions asked after the rush: where did this come from, what did the customer
     * think of the answer, when did it actually end.
     */
    {
      key: 'kind',
      header: t('support.kind'),
      hidden: true,
      text: (ticket) => t(`support.kind.${ticket.kind}`),
      render: (ticket) => t(`support.kind.${ticket.kind}`),
    },
    {
      key: 'urgency',
      header: t('support.urgency'),
      hidden: true,
      text: (ticket) => t(`support.urgency.${ticket.urgency}`),
      render: (ticket) => t(`support.urgency.${ticket.urgency}`),
    },
    {
      key: 'impact',
      header: t('support.impact'),
      hidden: true,
      text: (ticket) => t(`support.impact.${ticket.impact}`),
      render: (ticket) => t(`support.impact.${ticket.impact}`),
    },
    {
      key: 'source',
      header: t('support.source'),
      hidden: true,
      text: (ticket) => t(`support.from.${ticket.source}`),
      render: (ticket) => t(`support.from.${ticket.source}`),
    },
    {
      key: 'satisfaction',
      header: t('support.rating'),
      hidden: true,
      align: 'end',
      text: (ticket) => (ticket.satisfaction === null ? '' : `${ticket.satisfaction} / 5`),
      render: (ticket) =>
        ticket.satisfaction === null ? (
          <span className="muted">—</span>
        ) : (
          `${ticket.satisfaction} / 5`
        ),
    },
    {
      key: 'closed',
      header: t('record.field.closed_at'),
      hidden: true,
      text: (ticket) => formatDate(ticket.closedAt),
      render: (ticket) => <span className="muted small">{formatDate(ticket.closedAt)}</span>,
    },
    {
      key: 'updated',
      header: t('record.field.updated_at'),
      hidden: true,
      text: (ticket) => formatRelative(ticket.updatedAt),
      render: (ticket) => <span className="muted small">{formatRelative(ticket.updatedAt)}</span>,
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('nav.tickets')}
        description={t('support.description')}
        actions={
          <button
            type="button"
            className="button button--primary button--compact"
            onClick={() => setCreating(true)}
          >
            {t('support.newTicket')}
          </button>
        }
      />

      <NewTicketDialog open={creating} onClose={() => setCreating(false)} />

      {summary.isLoading ? (
        <Loading />
      ) : summary.error ? (
        <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
      ) : summary.data ? (
        <Figures
          summary={summary.data}
          onShowUnassigned={() => setTab('unassigned')}
          onShowBreached={() => setTab('breached')}
        />
      ) : null}

      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied(search.trim());
        }}
      >
        <span className="cell-inline">
          {TABS.map((key) => (
            <button
              key={key}
              type="button"
              className={`chip${tab === key ? ' chip--active' : ''}`}
              onClick={() => setTab(key)}
            >
              {t(`support.tab.${key}`)}
            </button>
          ))}
        </span>

        <label className="field-search">
          <input
            type="search"
            value={search}
            placeholder={t('support.searchPlaceholder')}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <button type="submit" className="button button--quiet button--compact">
          {t('crm.search')}
        </button>

        {tickets.data && (
          <span className="muted small">
            {t('crm.results').replace('{count}', String(tickets.data.total))}
          </span>
        )}
      </form>

      <Panel>
        {/*
          No `sort`: the server orders this list by urgency and nothing else, and a header that
          re-ordered the fifty rows the browser holds would quietly promise the whole queue.
        */}
        <DataTable
          tableKey="tickets"
          rows={tickets.data?.tickets}
          columns={columns}
          rowKey={(ticket) => ticket.id}
          isLoading={tickets.isLoading}
          error={tickets.error}
          onRetry={() => void tickets.refetch()}
          onRowClick={(ticket) => navigate(`/atendimento/chamados/${ticket.id}`)}
          empty={{ title: t('support.noTickets') }}
        />
      </Panel>
    </div>
  );
}

function statusTone(
  status: Ticket['status'],
): 'neutral' | 'positive' | 'warning' | 'danger' | 'info' {
  if (status === 'RESOLVED' || status === 'CLOSED') return 'positive';
  if (status === 'PENDING_CUSTOMER' || status === 'ON_HOLD') return 'warning';
  if (status === 'CANCELED') return 'neutral';

  return 'info';
}

/**
 * An SLA leg as the quick filter sees it.
 *
 * The cell is a badge and a countdown, so somebody typing "estourado" is looking for words the row
 * renders rather than for the `BREACHED` the API sent.
 */
function slaText(health: Ticket['sla']['firstResponse'], minutes: number | null): string {
  if (health === 'NONE') return '';

  const clock = minutes !== null && health !== 'MET' ? ` ${formatCountdown(minutes)}` : '';

  return `${t(`support.sla.${health}`)}${clock}`;
}

/**
 * One SLA leg in a cell.
 *
 * A promise nobody made shows a dash rather than a reassuring "OK": pretending there is a clock
 * where there is none is how a team finds out on the customer's call that nothing was ever measured.
 */
function SlaCell({
  health,
  minutes,
}: {
  health: Ticket['sla']['firstResponse'];
  minutes: number | null;
}): React.JSX.Element {
  if (health === 'NONE') return <span className="muted small">—</span>;

  return (
    <span className="cell-stack">
      <Badge tone={slaTone(health)}>{t(`support.sla.${health}`)}</Badge>
      {minutes !== null && health !== 'MET' && (
        <span className="muted small">{formatCountdown(minutes)}</span>
      )}
    </span>
  );
}

function Figures({
  summary,
  onShowUnassigned,
  onShowBreached,
}: {
  summary: TicketSummary;
  onShowUnassigned: () => void;
  onShowBreached: () => void;
}): React.JSX.Element {
  return (
    <div className="metrics">
      <button
        type="button"
        className={`metric metric--action${summary.breached > 0 ? ' metric--alert' : ''}`}
        onClick={onShowBreached}
      >
        <span className="metric__label">{t('support.kpi.breached')}</span>
        <span className="metric__value">{formatNumber(summary.breached)}</span>
        <span className="muted small">
          {summary.breached > 0 ? t('support.kpi.breachedHint') : t('support.kpi.breachedClear')}
        </span>
      </button>

      <button
        type="button"
        className={`metric metric--action${summary.unassigned > 0 ? ' metric--alert' : ''}`}
        onClick={onShowUnassigned}
      >
        <span className="metric__label">{t('support.kpi.unassigned')}</span>
        <span className="metric__value">{formatNumber(summary.unassigned)}</span>
        <span className="muted small">{t('support.kpi.unassignedHint')}</span>
      </button>

      <Figure label={t('support.kpi.open')} value={formatNumber(summary.open)} />

      <Figure
        label={t('support.kpi.waiting')}
        value={formatNumber(summary.pendingCustomer)}
        hint={t('support.kpi.waitingHint')}
      />

      <Figure
        label={t('support.kpi.review')}
        value={formatNumber(summary.awaitingReview)}
        hint={t('support.kpi.reviewHint')}
      />

      <Figure
        label={t('support.kpi.firstResponse')}
        value={
          summary.medianFirstResponseMinutes === null
            ? '—'
            : formatCountdown(summary.medianFirstResponseMinutes).replace(/^\S+\s/, '')
        }
        hint={t('support.kpi.median')}
      />

      <Figure
        label={t('support.kpi.satisfaction')}
        value={summary.satisfactionAverage === null ? '—' : `${summary.satisfactionAverage} / 5`}
        hint={t('support.kpi.resolvedToday').replace('{n}', String(summary.resolvedToday))}
      />
    </div>
  );
}

function Figure({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="metric">
      <span className="metric__label">{label}</span>
      <span className="metric__value">{value}</span>
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}
